/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import React from 'react';
import descriptors from '../../../locales/en-US/descriptors';
import {
  IAny,
  IObject
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import BreakdownByMeter from '../BreakdownByMeter/BreakdownByMeter';
import {
  IBreakdownByMeterProps,
  IMeterDataProps
} from '../BreakdownByMeter/BreakdownByMeterProps';
import { EnergyUsageField } from '../Enums';
import { ICommomGrapgh } from '../PerformanceGraph/IPerformanceGraph';
import PerformanceGraph from '../PerformanceGraph/PerformanceGraph';
import PerformanceGraphNILM from '../PerformanceGraph/PerformanceGraphNILM';
import { ICommonEnergyProgress } from '../ProgressBar/IProgressBarWidget';
import ProgressBarWidget from '../ProgressBar/ProgressBarWidget';
import { MAGIC_VALUE } from '../Utils/Constants';
import { getDateLabel } from '../Utils/EnergyUsagesViewDetailsUtils';
import _ from 'lodash';
import unset from 'unset-value';

export interface ICommonEnergy extends IAny {
  onViewDetailsClick?: () => void;
  progressBarData: ICommonEnergyProgress;
  graphData: ICommomGrapgh;
  tableData: IBreakdownByMeterProps;
  dateData: IObject;
  onSortingHandler?: (number: number) => void;
  isNILM?: boolean;
  emaasContext?: IAny;
  nilmCheck?: boolean;
  forSummary?: boolean;
  forGraph?: boolean;
  forTable?: boolean;
}

function CommonEnergyComponentSeparate(props: IAny) {
  const {
    onViewDetailsClick,
    progressBarData,
    graphData,
    tableData,
    dateData,
    onSortingHandler,
    nilmCheck,
    emaasContext,
    energyMeasurementUnit,
    sourceType
  } = props;

  let aggregationData, unit, deviationScaledData;

  if (props.forSummary && progressBarData) {
    ({ aggregationData, unit, deviationScaledData } = progressBarData);
  }

  let chartSeries, startTime, endTime, aggregationType;
  let columnHeaders, meterData;

  if (props.forGraph && graphData) {
    ({ chartSeries, startTime, endTime, aggregationType } = graphData);
  }

  if (props.forTable && tableData) {
    ({ columnHeaders, meterData } = tableData);
  }

  const {
    startMonth,
    startYear,
    endMonth,
    endYear,
    startDate,
    endDate
  } = dateData;
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  return (
    <div className="kpi-card-flex-container" style={{ height: 'auto' }}>
      {props.forSummary ? (
        <div className="kpi-card-progress-bar-container">
          <ProgressBarWidget
            category={props.isNILM ? 'EnergyUsageNILM' : 'EnergyUsage'}
            aggregationData={aggregationData}
            unit={unit}
            isProgressBarForEnergy={!props.isNILM}
            deviationScaledData={deviationScaledData}
            basicSub={_.toLower(sourceType) === 'water'} // To show previous period in progress bar instead of baseline
            fromCarbonKPI={props.fromCarbonKPI}
            originalUnit={props.originalUnit}
            conversionType={props.conversionType}
          />
        </div>
      ) : (
        ''
      )}
      {props.forGraph && (
        <div className="kpi-card-monthwise-container">
          <div className="kpi-card-monthwise-date-range">
            {_.toLower(sourceType) === 'water'
              ? t(d.WATERUSAGEDETAILS)
              : t(d.ENERGYUSAGEDETAILS)}{' '}
            {getDateLabel(
              startMonth,
              endMonth,
              aggregationType,
              startYear,
              endYear,
              startDate,
              endDate
            )}
          </div>
          {props.isNILM ? (
            <PerformanceGraphNILM
              chartSeries={chartSeries}
              startTime={startTime}
              endTime={endTime}
              aggregationType={aggregationType}
              height={'200'}
              spacing={[
                MAGIC_VALUE.TEN,
                MAGIC_VALUE.TEN,
                MAGIC_VALUE.FIFTEEN,
                MAGIC_VALUE.ZERO
              ]}
            />
          ) : (
            <PerformanceGraph
              chartSeries={chartSeries}
              startTime={startTime}
              endTime={endTime}
              aggregationType={aggregationType}
              height={'200'}
              spacing={[
                MAGIC_VALUE.TEN,
                MAGIC_VALUE.TEN,
                MAGIC_VALUE.FIFTEEN,
                MAGIC_VALUE.ZERO
              ]}
              conversionType={sourceType}
              dataSourceType={props.dataSourceType}
            />
          )}
          <div className="kpi-card-view-details">
            <Button
              type="link"
              content={t(d.VIEWDETAILS)}
              className="view-details-button"
              onClick={
                onViewDetailsClick ? () => onViewDetailsClick() : undefined
              }
            />
          </div>
        </div>
      )}
      {props.forTable ? (
        <div className="kpi-card-meter-container">
          {nilmCheck ? (
            emaasContext?.isNILMDisaggregationSubscribed ? (
              <BreakdownByMeter
                columnHeaders={columnHeaders}
                meterData={(meterData as unknown) as IMeterDataProps[]}
                category={EnergyUsageField.ENERGYUSAGEBREAKDOWN}
                onSorting={onSortingHandler!}
                unit={unit}
                energyMeasurementUnit={energyMeasurementUnit}
              />
            ) : (
              <div className="kpi-card-meter-container-empty"></div>
            )
          ) : (
            <BreakdownByMeter
              columnHeaders={columnHeaders}
              meterData={(meterData as unknown) as IMeterDataProps[]}
              category={EnergyUsageField.ENERGYUSAGEBREAKDOWN}
              onSorting={onSortingHandler!}
              unit={unit}
              energyMeasurementUnit={energyMeasurementUnit}
            />
          )}
        </div>
      ) : (
        ''
      )}
    </div>
  );
}

export default CommonEnergyComponentSeparate;
